import React, { useState, useEffect } from 'react';
import { useLocation, Navigate, useNavigate } from 'react-router-dom';
import LandingLayout from '../layouts/LandingLayout';
import { Button } from '@/components/ui/button';
import { CalendarCheck, Clock, Map, CalendarDays, XCircle, Loader2, Calendar, Mail, Phone } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import PaymentSummary from '@/components/booking/PaymentSummary';

interface BookingData {
  id: string;
  name: string;
  email: string;
  date: string;
  start_time: string;
  end_time: string;
  timezone: string;
  notes?: string;
  user_id: string;
  status: 'confirmed' | 'cancelled' | 'rescheduled';
  meeting_type?: string;
  payment_amount?: number;
  service_name?: string;
}

const BookingConfirmationPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [bookingData, setBookingData] = useState<BookingData | undefined>(
    location.state?.bookingData as BookingData | undefined
  );
  const [isCancelling, setIsCancelling] = useState(false);
  const [showCalendarOptions, setShowCalendarOptions] = useState(false);
  const [hostUsername, setHostUsername] = useState<string>('');

  useEffect(() => {
    // Fetch the host's username when the component mounts
    const fetchHostUsername = async () => {
      if (bookingData?.user_id) {
        const { data: userData, error } = await supabase
          .from('profiles')
          .select('business_name')
          .eq('user_id', bookingData.user_id)
          .single();

        if (!error && userData) {
          setHostUsername(userData.business_name || 'Host');
        }
      }
    };

    fetchHostUsername();
  }, [bookingData?.user_id]);

  if (!bookingData) {
    return <Navigate to="/" replace />;
  }

  const { name, email, date, start_time, end_time, timezone, notes, status, id, meeting_type, payment_amount, service_name } = bookingData;
  
  // Parse date string to Date object
  const bookingDate = parseISO(date);
  // Parse time strings
  const startTime = new Date(`${date}T${start_time}`);
  const endTime = new Date(`${date}T${end_time}`);

  const handleCancelBooking = async () => {
    if (!id) return;
    
    setIsCancelling(true);
    try {
      const { error } = await supabase
        .from('bookings')
        .update({ status: 'cancelled' })
        .eq('id', id)
        .select();

      if (error) throw error;
      
      // Update the local state
      setBookingData({ ...bookingData, status: 'cancelled' });
      toast.success('Booking successfully cancelled');
      
      // You could also send a cancellation email here
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error('Failed to cancel booking');
    } finally {
      setIsCancelling(false);
    }
  };

  // Generate Google Calendar URL
  const generateGoogleCalendarUrl = () => {
    const startDateTime = new Date(`${date}T${start_time}`);
    const endDateTime = new Date(`${date}T${end_time}`);
    
    const startIso = startDateTime.toISOString().replace(/-|:|\.\d+/g, '');
    const endIso = endDateTime.toISOString().replace(/-|:|\.\d+/g, '');
    
    const eventTitle = encodeURIComponent(`Appointment with ${hostUsername}`);
    const eventDescription = encodeURIComponent(
      `Meeting ${meeting_type ? `via ${meeting_type}` : ''}\n${notes ? `Notes: ${notes}` : ''}`
    );
    
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${eventTitle}&dates=${startIso}/${endIso}&details=${eventDescription}`;
  };

  // Generate Outlook Calendar URL
  const generateOutlookCalendarUrl = () => {
    const startDateTime = new Date(`${date}T${start_time}`);
    const endDateTime = new Date(`${date}T${end_time}`);
    
    const startIso = startDateTime.toISOString();
    const endIso = endDateTime.toISOString();
    
    const eventTitle = encodeURIComponent(`Appointment with ${hostUsername}`);
    const eventDescription = encodeURIComponent(
      `Meeting ${meeting_type ? `via ${meeting_type}` : ''}\n${notes ? `Notes: ${notes}` : ''}`
    );
    
    return `https://outlook.office.com/calendar/0/deeplink/compose?subject=${eventTitle}&startdt=${startIso}&enddt=${endIso}&body=${eventDescription}&path=%2Fcalendar%2Faction%2Fcompose&rru=addevent`;
  };

  // Generate iCal file
  const generateICalFile = () => {
    const startDateTime = new Date(`${date}T${start_time}`);
    const endDateTime = new Date(`${date}T${end_time}`);
    
    const formatDate = (date: Date) => {
      return date.toISOString().replace(/-|:|\.\d+/g, '');
    };
    
    const icalContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//CitaLoo//Calendar//EN',
      'CALSCALE:GREGORIAN',
      'BEGIN:VEVENT',
      `SUMMARY:Appointment with ${hostUsername}`,
      `DTSTART:${formatDate(startDateTime)}`,
      `DTEND:${formatDate(endDateTime)}`,
      `DESCRIPTION:${notes || 'No additional notes.'}`,
      `LOCATION:${meeting_type || 'Not specified'}`,
      'STATUS:CONFIRMED',
      `UID:${Date.now()}@citaloo.com`,
      'SEQUENCE:0',
      'BEGIN:VALARM',
      'TRIGGER:-PT15M',
      'ACTION:DISPLAY',
      'DESCRIPTION:Reminder',
      'END:VALARM',
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');
    
    const blob = new Blob([icalContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `appointment_${formatDate(startDateTime)}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <LandingLayout>
      <div className="container max-w-2xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <div className="flex justify-center">
            <div className={`h-16 w-16 rounded-full flex items-center justify-center mb-4 ${
              status === 'cancelled' 
                ? 'bg-red-100' 
                : 'bg-green-100'
            }`}>
              {status === 'cancelled' ? (
                <XCircle className="h-8 w-8 text-red-600" />
              ) : (
                <CalendarCheck className="h-8 w-8 text-green-600" />
              )}
            </div>
          </div>
          
          {status === 'cancelled' ? (
            <>
              <h1 className="text-2xl font-bold">Booking Cancelled</h1>
              <p className="text-muted-foreground mt-2">
                Your appointment with {hostUsername} has been cancelled.
              </p>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold">Booking Confirmed!</h1>
              <p className="text-muted-foreground mt-2">
                Your appointment with {hostUsername} has been scheduled.
              </p>
            </>
          )}
        </div>

        <div className="bg-white border rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Appointment Details</h2>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <CalendarDays className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium">Date</p>
                <p className="text-muted-foreground">
                  {format(bookingDate, 'EEEE, MMMM d, yyyy')}
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium">Time</p>
                <p className="text-muted-foreground">
                  {format(startTime, 'h:mm a')} - {format(endTime, 'h:mm a')}
                  <span className="block text-sm">({timezone})</span>
                </p>
              </div>
            </div>

            {meeting_type && (
              <div className="flex items-start gap-3">
                <Map className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium">Meeting Type</p>
                  <p className="text-muted-foreground">{meeting_type}</p>
                </div>
              </div>
            )}
            
            {notes && (
              <div className="flex items-start gap-3">
                <Map className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium">Notes</p>
                  <p className="text-muted-foreground">{notes}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white border rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Your Information</h2>
          <p><span className="font-medium">Name:</span> {name}</p>
          <p><span className="font-medium">Email:</span> {email}</p>
        </div>

        {payment_amount && payment_amount > 0 && (
          <PaymentSummary
            serviceDetails={{ name: service_name || 'Consultation', description: '', price: payment_amount, cancellationPolicy: '' }}
            paymentRequired={true}
            amount={payment_amount}
          />
        )}

        <div className="bg-white border rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">What's Next</h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium">Check Your Email</p>
                <p className="text-muted-foreground">
                  A confirmation email has been sent to your inbox with all appointment details.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium">Add to Calendar</p>
                <p className="text-muted-foreground">
                  Don't forget to add this appointment to your calendar to receive reminders.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Phone className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium">Prepare for Your Meeting</p>
                <p className="text-muted-foreground">
                  {meeting_type === 'Zoom' || meeting_type === 'Google Meet' 
                    ? 'The meeting link will be sent to your email before the appointment.' 
                    : meeting_type === 'Phone' 
                      ? 'You will receive a call at the provided number at the scheduled time.'
                      : 'Please prepare any necessary materials for your appointment.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-lg shadow-sm p-6 mb-8">
          <h2 className="text-lg font-semibold mb-4">Add to Calendar</h2>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={() => window.open(generateGoogleCalendarUrl(), '_blank')}>
              <Calendar className="mr-2 h-4 w-4" />
              Google Calendar
            </Button>
            <Button variant="outline" onClick={() => window.open(generateOutlookCalendarUrl(), '_blank')}>
              <Calendar className="mr-2 h-4 w-4" />
              Outlook
            </Button>
            <Button variant="outline" onClick={generateICalFile}>
              <Calendar className="mr-2 h-4 w-4" />
              iCal File (.ics)
            </Button>
          </div>
        </div>

        <div className="text-center space-y-3">
          {status !== 'cancelled' && (
            <Button 
              variant="outline" 
              className="w-full sm:w-auto border-red-200 text-red-600 hover:bg-red-50" 
              disabled={isCancelling}
              onClick={handleCancelBooking}
            >
              {isCancelling ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cancelling...
                </>
              ) : (
                <>
                  <XCircle className="mr-2 h-4 w-4" />
                  Cancel Booking
                </>
              )}
            </Button>
          )}
          
          <div>
            <Button asChild className="px-8">
              <a href="/">Return Home</a>
            </Button>
          </div>
        </div>
      </div>
    </LandingLayout>
  );
};

export default BookingConfirmationPage;
